import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import EventForm, {
	EventFormValues
} from "../../components/EventForm/EventForm";
import useEvent from "../../hooks/useEvent";
import useEditEvent from "../../hooks/useEditEvent";
import useDeleteEvent from "../../hooks/useDeleteEvent";
import { adaptEditEventPayload } from "../../adapters/adaptEventPayload";
import { formValuesToEvent } from "../../adapters/formValuesToEvent";

function eventToFormValues(event: MeeEvent): EventFormValues {
	const startParts = (event.startDate ?? "").split(/T| /);
	const endParts = (event.endDate ?? "").split(/T| /);

	return {
		name: event.name,
		location: event.location ?? "",
		body: event.body ?? "",
		startDate: startParts[0] ?? "",
		startTime: (startParts[1] ?? "").substring(0, 5),
		showStartDate: event.showStartDate,
		endDate: endParts[0] ?? "",
		endTime: (endParts[1] ?? "").substring(0, 5),
		showEndDate: event.showEndDate,
		tag: event.tag,
		image: null,
		audienceGroupIds: (event.audienceGroups ?? []).map((g) => g.id)
	};
}

const EditEvent = () => {
	const navigate = useNavigate();
	const { eventId } = useParams<{ eventId: string }>();
	const id = Number(eventId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<EventFormValues>({
		name: "",
		location: "",
		body: "",
		startDate: "",
		startTime: "",
		showStartDate: true,
		endDate: "",
		endTime: "",
		showEndDate: true,
		tag: null,
		image: null,
		audienceGroupIds: []
	});

	const { data: event } = useEvent(isValidId ? id : 0);
	const { mutate: editEvent, isLoading: isEditing } = useEditEvent();
	const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteEvent();

	useEffect(() => {
		if (event) {
			setDefaultValues(eventToFormValues(event));
		}
	}, [event]);

	if (!isValidId) {
		return <Navigate to="/app/events" replace />;
	}

	const handleSubmit = (data: EventFormValues, imageRemoved: boolean) => {
		const payload = adaptEditEventPayload(
			formValuesToEvent(data, id),
			data.image,
			imageRemoved,
			data.audienceGroupIds
		);

		editEvent(
			{ id, payload },
			{
				onSuccess: () => navigate("/app/events")
			}
		);
	};

	const handleDelete = () => {
		deleteEvent(id, {
			onSuccess: () => navigate("/app/events")
		});
	};

	return (
		<Page>
			<EventForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				existingImageReference={event?.image ?? null}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete event?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditEvent;
